import { FC, ReactNode, useEffect, useState } from "react";
import CustomPopup from "../../../../custom-components/CustomPopup";
import { useTranslation } from "react-i18next";
import { WorkCategory, WorkCategoryWithSubcategories } from "../../../../domain/enums/WorkCategoryEnum";
import { enumToSelectProps } from "../../../../tools/EnumTools";
import WorkCategoriesIcons from "../work-components/WorkCategoriesIcons";
import { WorksCategoriesForm } from "../../../../domain/models/dto/WorksCategoriesDTO";
import { SelectValue } from "../../../../types/SelectProps";
import ClosingCrossDarkGreySVG from "../../svg/functional/ClosingCrossDarkGreySVG";

interface WorkCategoriesPopupProps {
    defaultSelected: WorksCategoriesForm[];
    buttonElement: ReactNode;
    buttonElementClassName: string;
    confirmButtonTitle: string;
    confirmButtonClassName?: string;
    onopenPopupHandler?: () => void;
    confirmHandler: (values: WorksCategoriesForm[]) => void;
}

const WorkCategoriesPopup: FC<WorkCategoriesPopupProps> = (props) => {
    const [isVisible, setIsVisible] = useState(false);
    const [selected, setSelected] = useState<WorksCategoriesForm[]>(props.defaultSelected);
    const { t } = useTranslation();
    useEffect(() => {
        setSelected(props.defaultSelected);
    }, [props.defaultSelected]);
    const selectCategoryHandler = (category: WorkCategory) => {
        setSelected((prev) =>
            categoryIncludes(prev, category)
                ? prev.filter((item) => item.category !== category)
                : [...prev, { category }]
        );
    };
    const categoryIncludes = (arr: WorksCategoriesForm[], value: WorkCategory | SelectValue) => {
        return !!arr.find((item) => item.category === value);
    };
    const confirmHandler = () => {
        props.confirmHandler(selected);
        setIsVisible(false);
    };
    const closeHandler = () => {
        setSelected(props.defaultSelected);
    };

    return (
        <CustomPopup
            isVisible={isVisible}
            setIsVisible={setIsVisible}
            wrapClassName="dmin2:w-[1062px] di6:w-[780px] ti6:w-[360px] ti7:w-[360px] tmax4:w-[360px]"
            confirmTitle={props.confirmButtonTitle}
            confirmHandler={confirmHandler}
            confirmButtonClassName={props.confirmButtonClassName}
            closeHandler={closeHandler}
            onOpen={props.onopenPopupHandler}
            buttonElement={
                <div onClick={() => setIsVisible(true)} className={props.buttonElementClassName}>
                    {props.buttonElement}
                </div>
            }
        >
            <div className="flex flex-wrap gap-6 dmax2:gap-3 justify-center mt-8">
                {enumToSelectProps(WorkCategory, "work.categories").map((item, index) => (
                    <div
                        key={`work-category-${index}`}
                        onClick={() => selectCategoryHandler(item.value as WorkCategory)}
                        className={`${
                            categoryIncludes(selected, item.value) ? "bg-textFaded" : "bg-backgroundInput"
                        } relative flex flex-col items-center w-[227px] dmax2:w-[159px] h-[170px] rounded-[6px] cursor-pointer overflow-hidden group transition-all duration-300 ease-in-out`}
                    >
                        <WorkCategoriesIcons
                            category={item.value as WorkCategory}
                            selected={categoryIncludes(selected, item.value)}
                            className="mt-[24px] dmin5:group-hover:w-7 dmin5:group-hover:h-7 transition-all duration-300 ease-in-out"
                        />
                        <span
                            className={`H16B ${
                                categoryIncludes(selected, item.value) && "text-[17px]"
                            } text-textWhite text-center mt-4 px-4 dmin5:group-hover:opacity-0 dmin5:group-hover:translate-y-[-70px] transition-all duration-500 ease-out`}
                        >
                            {t(item.labelKey)}
                        </span>
                        <div
                            className={`absolute bottom-[-34px] H14 w-[90%] max-h-[70px] overflow-auto ${
                                categoryIncludes(selected, item.value) ? "scrollbar-thin-inverted" : "scrollbar-thin"
                            } text-textWhite text-center mt-4 px-4 opacity-0 dmin5:group-hover:opacity-100 dmin5:group-hover:translate-y-[-70px] transition-all duration-500 ease-out`}
                        >
                            {enumToSelectProps(
                                WorkCategoryWithSubcategories[item.value as WorkCategory],
                                `work.subcategories.${item.value}`
                            ).map((subcategories, index) => (
                                <div key={`subcategory-${index}`}>{t(subcategories.labelKey)}</div>
                            ))}
                        </div>
                        {categoryIncludes(selected, item.value) && (
                            <ClosingCrossDarkGreySVG className="absolute top-[10px] right-[10px]" />
                        )}
                    </div>
                ))}
            </div>
        </CustomPopup>
    );
};

export default WorkCategoriesPopup;
